/**
 * captureRunStore — diário da captura de Emails/Telefones para a busca em tela cheia (proposta
 * "Full Form", 18/10/2026): quais pesquisas já rodaram, quantos leads novos cada uma trouxe,
 * qual está em andamento e os últimos leads que chegaram.
 *
 * A extensão não diz de qual pesquisa veio cada lote. Como o CaptureRuntime abre uma pesquisa
 * por vez e só avança depois do lote que a conclui, todo lote é da pesquisa corrente; o runtime
 * registra aqui no mesmo ponto em que move o cursor (captureRuntime.ts, `handleStored`).
 *
 * Fica no localStorage (um diário por módulo) para o resumo continuar depois de recarregar a
 * página. Não é fonte de verdade da captura: fase e cursor continuam no IndexedDB.
 */
import { create } from 'zustand';
import type { CaptureScheduleRow } from '../lib/captureEngine';
import type { LocalLead } from '../data/localDb';

export type RunModule = 'emails' | 'phones';

export interface RunQuery {
  /** Posição no schedule (cursor do motor). */
  i: number;
  engine?: string;
  site?: string;
  keywords?: string;
  domain?: string;
  phone?: string;
  code?: string;
}

export interface RecentLead {
  key: string;
  type: LocalLead['type'];
  value: string;
  title?: string;
  url?: string;
  /** Fonte (termo do site) da pesquisa que trouxe o lead. */
  site?: string;
}

export interface CaptureRunLog {
  runId: string;
  listId: string;
  userId: string;
  module: RunModule;
  startedAt: string;
  queries: RunQuery[];
  /** Pesquisas concluídas, por posição. */
  done: boolean[];
  /** Leads novos por posição. */
  found: Record<number, number>;
  /** Pesquisa aberta agora; null quando nenhuma está em voo. */
  current: number | null;
  recent: RecentLead[];
  /** Leads na lista de destino (total, não só desta captura). */
  listCount: number;
  /** Critérios da busca, para "Vai para" e o resumo. */
  keywords: string[];
  sites: string[];
  areaCode?: string;
  endedAt?: string;
  outcome?: 'finished' | 'stopped';
  /** A pessoa saiu do resumo ("Nova busca"): a tela volta para a vitrine. */
  dismissed?: boolean;
}

const STORAGE_KEY = 'capture_run_log';
const RECENT_MAX = 20;

export function isRunModule(module: string): module is RunModule {
  return module === 'emails' || module === 'phones';
}

function load(): Partial<Record<RunModule, CaptureRunLog>> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? (JSON.parse(raw) as unknown) : null;
    return parsed && typeof parsed === 'object'
      ? (parsed as Partial<Record<RunModule, CaptureRunLog>>)
      : {};
  } catch {
    return {};
  }
}

function save(logs: Partial<Record<RunModule, CaptureRunLog>>): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(logs));
  } catch {
    // Sem storage o diário vale só nesta aba.
  }
}

function toQuery(row: CaptureScheduleRow, i: number): RunQuery {
  const query: RunQuery = { i };
  if (row.engine) query.engine = row.engine;
  if (row.site) query.site = row.site;
  if (row.keywords) query.keywords = row.keywords;
  if (row.domain) query.domain = row.domain;
  if (row.phone) query.phone = row.phone;
  if (row.code) query.code = row.code;
  return query;
}

export interface RunBegin {
  runId: string;
  listId: string;
  userId: string;
  module: RunModule;
  schedule: readonly CaptureScheduleRow[];
  current: number | null;
  listCount: number;
  keywords?: readonly string[];
  sites?: readonly string[];
  areaCode?: string;
}

export interface RunSync {
  runId: string;
  listId: string;
  userId: string;
  module: RunModule;
  schedule: readonly CaptureScheduleRow[];
  current: number | null;
  listCount?: number;
  keywords?: readonly string[];
  sites?: readonly string[];
  areaCode?: string;
}

export interface RunStored {
  runId: string;
  module: RunModule;
  row: number | null;
  added?: readonly LocalLead[];
  inserted: number;
  listCount: number;
}

interface CaptureRunState {
  logs: Partial<Record<RunModule, CaptureRunLog>>;
  begin: (info: RunBegin) => void;
  /** Cria o diário de uma execução restaurada (outra aba/recarga) ou atualiza o cursor. */
  sync: (info: RunSync) => void;
  stored: (info: RunStored) => void;
  end: (info: {
    runId: string;
    module: RunModule;
    outcome: 'finished' | 'stopped';
    listCount: number;
  }) => void;
  dismiss: (module: RunModule) => void;
  /** Aviso "A captura continua" ao sair da tela cheia com a captura andando. */
  toastAt: number | null;
  showToast: () => void;
  hideToast: () => void;
}

function commit(
  logs: Partial<Record<RunModule, CaptureRunLog>>,
  module: RunModule,
  log: CaptureRunLog,
): { logs: Partial<Record<RunModule, CaptureRunLog>> } {
  const next = { ...logs, [module]: log };
  save(next);
  return { logs: next };
}

export const useCaptureRunStore = create<CaptureRunState>((set) => ({
  logs: load(),
  toastAt: null,
  showToast: () => set({ toastAt: Date.now() }),
  hideToast: () => set({ toastAt: null }),
  begin: (info) =>
    set((state) =>
      commit(state.logs, info.module, {
        runId: info.runId,
        listId: info.listId,
        userId: info.userId,
        module: info.module,
        startedAt: new Date().toISOString(),
        queries: info.schedule.map(toQuery),
        done: info.schedule.map((row) => row.finished === true),
        found: {},
        current: info.current,
        recent: [],
        listCount: info.listCount,
        keywords: [...(info.keywords ?? [])],
        sites: [...(info.sites ?? [])],
        ...(info.areaCode ? { areaCode: info.areaCode } : {}),
      }),
    ),
  sync: (info) =>
    set((state) => {
      const previous = state.logs[info.module];
      const done = info.schedule.map((row) => row.finished === true);
      if (previous && previous.runId === info.runId) {
        const same =
          previous.current === info.current &&
          previous.done.length === done.length &&
          previous.done.every((value, index) => value === done[index]) &&
          (info.listCount === undefined || info.listCount === previous.listCount);
        if (same) return state;
        return commit(state.logs, info.module, {
          ...previous,
          done,
          current: info.current,
          ...(info.listCount !== undefined ? { listCount: info.listCount } : {}),
        });
      }
      return commit(state.logs, info.module, {
        runId: info.runId,
        listId: info.listId,
        userId: info.userId,
        module: info.module,
        startedAt: new Date().toISOString(),
        queries: info.schedule.map(toQuery),
        done,
        found: {},
        current: info.current,
        recent: [],
        listCount: info.listCount ?? 0,
        keywords: [...(info.keywords ?? [])],
        sites: [...(info.sites ?? [])],
        ...(info.areaCode ? { areaCode: info.areaCode } : {}),
      });
    }),
  stored: (info) =>
    set((state) => {
      const log = state.logs[info.module];
      if (!log || log.runId !== info.runId) return state;
      const found = { ...log.found };
      if (info.row !== null && info.inserted > 0) {
        found[info.row] = (found[info.row] ?? 0) + info.inserted;
      }
      const site = info.row !== null ? log.queries[info.row]?.site : undefined;
      const fresh: RecentLead[] = (info.added ?? []).map((lead) => ({
        key: lead.key,
        type: lead.type,
        value: lead.value,
        ...(lead.title ? { title: lead.title } : {}),
        ...(lead.url ? { url: lead.url } : {}),
        ...(site ? { site } : {}),
      }));
      return commit(state.logs, info.module, {
        ...log,
        found,
        recent: [...fresh.reverse(), ...log.recent].slice(0, RECENT_MAX),
        listCount: info.listCount,
      });
    }),
  end: (info) =>
    set((state) => {
      const log = state.logs[info.module];
      if (!log || log.runId !== info.runId) return state;
      return commit(state.logs, info.module, {
        ...log,
        current: null,
        done: info.outcome === 'finished' ? log.done.map(() => true) : log.done,
        endedAt: new Date().toISOString(),
        outcome: info.outcome,
        listCount: info.listCount,
      });
    }),
  dismiss: (module) =>
    set((state) => {
      const log = state.logs[module];
      if (!log || log.dismissed) return state;
      return commit(state.logs, module, { ...log, dismissed: true });
    }),
}));

/** Leads novos somados de todas as pesquisas do diário. */
export function runFoundTotal(log: CaptureRunLog): number {
  return Object.values(log.found).reduce((total, n) => total + n, 0);
}
